/**
 * Custom hook for timeline initial positioning and "Fit to Passage" functionality
 */

import { useState, useRef, useCallback, useLayoutEffect, type RefObject } from 'react';
import { yearToPixel } from './timeline-utils';
import { calculateOptimalZoom, getHeaderWidth } from './timeline-config';

interface UseTimelineInitialPositionParams {
    passageId?: string;
    passageDate?: DateProposal;
    minYear: number;
    maxYear: number;
    timelineWidth: number;
    pixelsPerYear: number;
    setPixelsPerYear: (value: number) => void;
    scrollContainerRef: RefObject<HTMLDivElement>;
    shouldCenterOnPassageRef: RefObject<boolean>;
}

interface UseTimelineInitialPositionReturn {
    handleFitToPassage: () => void;
    resetKey: number;
}

/**
 * Manages initial scroll positioning and "Fit to Passage" functionality
 */
export function useTimelineInitialPosition({
    passageId,
    passageDate,
    minYear,
    maxYear,
    timelineWidth,
    pixelsPerYear,
    setPixelsPerYear,
    scrollContainerRef,
    shouldCenterOnPassageRef
}: UseTimelineInitialPositionParams): UseTimelineInitialPositionReturn {
    const hasInitializedScrollRef = useRef(false);
    const lastPassageIdRef = useRef<string | undefined>(passageId);
    const [resetKey, setResetKey] = useState(0);

    // Reset initialization when passage changes
    if (passageId !== lastPassageIdRef.current) {
        hasInitializedScrollRef.current = false;
        shouldCenterOnPassageRef.current = true;
        lastPassageIdRef.current = passageId;
    }

    // Handle "Fit to Passage" button click
    const handleFitToPassage = useCallback(() => {
        if (!passageDate || !scrollContainerRef.current) return;

        shouldCenterOnPassageRef.current = true;
        
        // Calculate and set optimal zoom for this passage
        const optimalZoom = calculateOptimalZoom(
            passageDate.startYear,
            passageDate.endYear,
            scrollContainerRef.current.clientWidth
        );
        setPixelsPerYear(optimalZoom);
        
        // Increment reset key to force re-centering
        setResetKey(prev => prev + 1);
    }, [passageDate, scrollContainerRef, shouldCenterOnPassageRef, setPixelsPerYear]);

    // Auto-scroll to center passage date (only on initial load or when Reset is clicked)
    useLayoutEffect(() => {
        if (!scrollContainerRef.current || !passageDate) return;
        if (hasInitializedScrollRef.current && !shouldCenterOnPassageRef.current) return;

        // If this is initial load, calculate and apply optimal zoom first
        if (!hasInitializedScrollRef.current) {
            const optimalZoom = calculateOptimalZoom(
                passageDate.startYear,
                passageDate.endYear,
                scrollContainerRef.current.clientWidth
            );
            
            // If we need to update zoom, do it and return early to let component re-render
            if (Math.abs(pixelsPerYear - optimalZoom) > 0.01) {
                setPixelsPerYear(optimalZoom);
                return;
            }
        }

        // Calculate scroll position to center the passage date
        const passageDateCenter = yearToPixel(
            passageDate.startYear,
            minYear,
            maxYear,
            timelineWidth
        );
        
        const headerWidth = getHeaderWidth();
        const containerWidth = scrollContainerRef.current.clientWidth;
        const scrollLeft = passageDateCenter + headerWidth - containerWidth / 2;

        // Instant scroll
        scrollContainerRef.current.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: 'instant'
        });

        // Mark as initialized and disable auto-centering
        hasInitializedScrollRef.current = true;
        shouldCenterOnPassageRef.current = false;
    }, [
        passageDate,
        minYear,
        maxYear,
        timelineWidth,
        pixelsPerYear,
        setPixelsPerYear,
        scrollContainerRef,
        shouldCenterOnPassageRef,
        resetKey
    ]);

    return {
        handleFitToPassage,
        resetKey
    };
}

